import { useEffect, useId, useRef, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { CATALOG_TABS } from "@/data/catalog";
import { useCart } from "@/lib/cart";
import { config } from "@/lib/config";
import { CartDrawer } from "@/components/CartDrawer";
import { SmsChat } from "@/components/SmsChat";
import "./AppLayout.css";

export function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { totalItems } = useCart();

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(e: MouseEvent) {
      const el = menuWrapRef.current;
      if (el && !el.contains(e.target as Node)) setMenuOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  return (
    <div className="app-layout">
      <header className="app-layout__header">
        <div className="app-layout__header-inner">
          <div className="app-layout__brand">
            <div className="app-layout__brand-text">
              <Link
                to="/"
                className="app-layout__mark app-layout__mark-link"
                aria-label={`${config.brandLockup} home`}
              >
                {config.brandLockup}
              </Link>
              <span className="app-layout__tagline">Cannabis Delivery For Travis County, Texas</span>
            </div>
          </div>

          <div className="app-layout__header-toolbar" ref={menuWrapRef}>
            <span className="app-layout__phone-display">{config.contactPhoneDisplay}</span>

            <button
              type="button"
              className="app-layout__cart-btn"
              aria-label={`Cart – ${totalItems} item${totalItems !== 1 ? "s" : ""}`}
              onClick={() => setCartOpen(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {totalItems > 0 && <span className="app-layout__cart-badge">{totalItems}</span>}
            </button>

            <div className="app-layout__menu-wrap">
              <button
                type="button"
                className="app-layout__menu-btn"
                aria-expanded={menuOpen}
                aria-controls={menuId}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <span className="app-layout__burger" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </button>

              {menuOpen ? (
                <nav id={menuId} className="app-layout__dropdown" aria-label="Product categories">
                  {CATALOG_TABS.map(({ id, label }) => (
                    <Link
                      key={id}
                      to={`/?tab=${id}`}
                      className="app-layout__menu-link"
                      onClick={() => setMenuOpen(false)}
                    >
                      {label}
                    </Link>
                  ))}
                </nav>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <main className="app-layout__main" id="main-content">
        <Outlet />
      </main>

      <footer className="app-layout__footer">
        <div className="app-layout__footer-inner">
          <p className="app-layout__copyright">
            &copy; {new Date().getFullYear()} {config.appName}. 21+ only. Licensed operator.
          </p>

          <p className="app-layout__disclaimer">
            Product availability, pricing, and regulatory requirements may change. This site does
            not provide legal or medical advice.
          </p>

          <nav className="app-layout__legal-nav" aria-label="Legal">
            <NavLink
              to="/terms"
              className={({ isActive }) =>
                `app-layout__legal-link${isActive ? " app-layout__legal-link--active" : ""}`
              }
            >
              Terms
            </NavLink>
            <span className="app-layout__legal-sep" aria-hidden="true">
              &middot;
            </span>
            <NavLink
              to="/privacy"
              className={({ isActive }) =>
                `app-layout__legal-link${isActive ? " app-layout__legal-link--active" : ""}`
              }
            >
              Privacy
            </NavLink>
          </nav>
        </div>
      </footer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <SmsChat />
    </div>
  );
}
